<template>
  <div>
    <div v-if="filteredChartData.labels.length">
      <Chart_V5
        :chart_data="filteredChartData"
        :chart_config="chartConfig"
        override-height="h-[180px]"
      />
    </div>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';

export default {
  name: 'CCLFReport_EntityCost',
  components: { Chart_V5 },
  props: {
    entityCostPbpm: { type: Object, default: null },
    performanceYear: { type: Number, default: 2026 },
    hiddenSeries: { type: Array, default: () => [] },
  },

  computed: {
    currentYearData() {
      return this.entityCostPbpm?.current_year || []
    },
    priorYearData() {
      return this.entityCostPbpm?.prior_year || []
    },
    currentPy() {
      return this.entityCostPbpm?.current_py || this.performanceYear - 1
    },
    priorPy() {
      return this.entityCostPbpm?.prior_py || this.performanceYear - 2
    },

    allSeries() {
      if (!this.currentYearData.length) return []
      const series = [
        {
          values: this.currentYearData.map(r => r.PBPM),
          config: {
            label: 'Monthly PBPM',
            backgroundColor: 'rgba(75, 192, 192, 0.4)',
            type: 'line',
            yAxis: 'left',
            offset: 0,
          },
        },
      ]
      if (this.priorYearData.length) {
        series.push({
          values: this.priorYearData.map(r => r.PBPM),
          config: {
            label: 'Prior Year PBPM',
            backgroundColor: 'rgba(180, 43, 212, 0.35)',
            type: 'line',
            yAxis: 'left',
            offset: 0,
          },
        })
      }
      // Rolling 12-month PBPM
      const rolling = this.currentYearData.map(r => r.ROLLING_12_PBPM)
      if (rolling.some(v => v != null && v > 0)) {
        series.push({
          values: rolling,
          config: {
            label: 'Rolling 12mo PBPM',
            // Dark blue (was amber) — amber read too close to the red E&U line
            // for Curana (Jennifer, 2026-06-26). Blue is clearly distinct.
            backgroundColor: 'rgba(30, 64, 175, 0.6)',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeDasharray: '6 3',
          },
        })
      }
      // BEUR benchmark (already monthly PBPM from backend)
      const beur = this.currentYearData.map(r => r.BEUR_PBPM ?? null)
      if (beur.some(v => v != null)) {
        series.push({
          values: beur,
          config: {
            label: 'BEUR PBPM',
            backgroundColor: 'rgba(239, 68, 68, 0.4)',
            type: 'line',
            yAxis: 'left',
            offset: 0,
            strokeDasharray: '2 2',
          },
        })
      }
      return series
    },

    filteredChartData() {
      if (!this.currentYearData.length) return { series: [], labels: [] }
      const labels = this.currentYearData.map(r => r.CLM_THRU_DT.slice(0, 7))
      const hidden = new Set(this.hiddenSeries)
      const series = this.allSeries.filter(s => !hidden.has(s.config.label))
      return { series, labels }
    },

    yMin() {
      const vals = this.filteredChartData.series
        .flatMap(s => s.values)
        .filter(v => v != null && isFinite(v) && v > 0)
      if (!vals.length) return 0
      const min = Math.min(...vals)
      const max = Math.max(...vals)
      const padding = (max - min) * 0.1 || 100
      return Math.max(0, Math.floor(min - padding))
    },

    chartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 8, right: 20, bottom: 24, left: 60 },
        maxBarWidth: 24,
        xAxis: { labelRotation: 0, fontSize: 9 },
        hideGridLines: true,
        yAxes: [{ position: 'left', min: this.yMin, fontSize: 9 }],
        legend: { display: false },
        formatting: { currency: true },
        title: '',
        subtitle: '',
      }
    },
  },
}
</script>
